import { Alert, AlertDescription, Button } from '@up2stay/up2promo-ui';
import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { usePushPermissionStatus } from '@/core/hooks/use-push-permission-status';
import { requestPushPermissionAndRegister } from '@/core/lib/push-notifications';
import {
  applyPushGrantDefaults,
  openSystemNotificationSettings,
} from '@/core/lib/push-permission-flow';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

type PushSettingsBannerProps = {
  pushFeatureEnabled: boolean;
  dict: {
    title: string;
    descriptionPrompt: string;
    descriptionDenied: string;
    enable: string;
    openSystemSettings: string;
  };
  onEnabled?: () => void;
};

export function PushSettingsBanner({
  pushFeatureEnabled,
  dict,
  onEnabled,
}: PushSettingsBannerProps) {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { status: permissionStatus, refresh } = usePushPermissionStatus(
    pushFeatureEnabled && Boolean(workspaceId),
  );
  const [isEnabling, setIsEnabling] = useState(false);
  const syncedGrantRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      permissionStatus !== 'granted' ||
      !workspaceId ||
      syncedGrantRef.current === workspaceId
    ) {
      return;
    }

    syncedGrantRef.current = workspaceId;
    void (async () => {
      await requestPushPermissionAndRegister();
      await applyPushGrantDefaults(workspaceId);
      onEnabled?.();
    })();
  }, [onEnabled, permissionStatus, workspaceId]);

  if (
    !pushFeatureEnabled ||
    !isCapacitorNative() ||
    !workspaceId ||
    permissionStatus === null ||
    permissionStatus === 'granted'
  ) {
    return null;
  }

  const isDenied = permissionStatus === 'denied';

  const handleEnable = async () => {
    if (isDenied) {
      await openSystemNotificationSettings();
      return;
    }

    setIsEnabling(true);
    try {
      const result = await requestPushPermissionAndRegister();
      if (!result.granted) {
        await refresh();
        return;
      }

      await applyPushGrantDefaults(workspaceId);
      syncedGrantRef.current = workspaceId;
      await refresh();
      onEnabled?.();
    } finally {
      setIsEnabling(false);
    }
  };

  return (
    <Alert className="border-primary/30 bg-primary/5">
      <Bell className="h-4 w-4" />
      <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="font-medium text-foreground">{dict.title}</p>
          <p className="text-sm text-muted-foreground">
            {isDenied ? dict.descriptionDenied : dict.descriptionPrompt}
          </p>
        </div>
        <Button
          size="sm"
          variant={isDenied ? 'outline' : 'primary'}
          disabled={isEnabling}
          onClick={() => void handleEnable()}
        >
          {isDenied ? dict.openSystemSettings : dict.enable}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
